import { Button, Check, Grid, Typography } from 'components/atoms';
import { ChangeEvent, MouseEventHandler, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DocumentExtraDataJson } from 'types';
import { errorsMap, inputComponent, regExp } from './ValidateData.const';
import { CheckboxField, CheckboxLabel, Root } from './ValidateData.styles';

export type ValidateDataProps = {
  onConfirm: (data: any) => void;
  onReject: MouseEventHandler<HTMLButtonElement>;
  input: DocumentExtraDataJson['ocr'];
  loading?: boolean;
  hideReject?: boolean;
};

const ValidateData = ({
  onConfirm,
  onReject,
  input,
  loading,
  hideReject = false,
}: ValidateDataProps) => {
  const inputs = useMemo(() => Object.keys(input), [input]);

  const defaultValues = useMemo(
    () =>
      inputs.reduce((acc, curr) => {
        const item = input[curr as keyof typeof input];
        const rawValue =
          item?.viewValue && !item.mustFix ? item.viewValue : item.value || '';

        return {
          ...acc,
          [curr]:
            item.type === 'boolean'
              ? String(rawValue).toLowerCase() === 'true'
              : rawValue,
        };
      }, {}),
    [inputs, input],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Record<string, string | boolean>>({
    reValidateMode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const haveInputPerValidate =
    inputs.filter((key) => input[key].mustFix).length === 0;

  return (
    <Root>
      <form
        noValidate
        onSubmit={handleSubmit(onConfirm)}
        style={{ flexGrow: 1 }}
      >
        <Grid container spacing={5} justify="flex-end">
          <Grid item xs={12}>
            <Typography
              variant="h2"
              align={haveInputPerValidate ? 'left' : 'center'}
            >
              {haveInputPerValidate
                ? '¿Desea validar el documento?'
                : 'Valide los datos del documento'}
            </Typography>
          </Grid>
          {inputs
            .filter((key) => input[key].mustFix)
            .map((key) => {
              const inputItem = input[key as keyof typeof input];
              const isCheckbox = inputItem.type === 'boolean';
              const Component =
                inputComponent[inputItem.type as keyof typeof inputComponent];

              return (
                <Grid item xs={12} key={key}>
                  <Controller
                    control={control}
                    name={key}
                    rules={
                      isCheckbox
                        ? {}
                        : {
                            required: 'Campo requerido',
                            pattern: {
                              value: regExp[
                                inputItem.type as keyof typeof regExp
                              ] as RegExp,
                              message: errorsMap[
                                inputItem.type as keyof typeof regExp
                              ] as string,
                            },
                          }
                    }
                    render={({ field }) =>
                      isCheckbox ? (
                        <CheckboxField>
                          <Check
                            checked={!!field.value}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              field.onChange(e.target.checked)
                            }
                          />
                          <CheckboxLabel variant="p1">
                            {inputItem.label}
                          </CheckboxLabel>
                        </CheckboxField>
                      ) : (
                        <Component
                          fullWidth
                          label={inputItem.label}
                          value={field.value as string}
                          onChange={field.onChange}
                          error={!!errors[key]}
                          helperText={errors[key] && errors[key]?.message}
                        />
                      )
                    }
                  />
                </Grid>
              );
            })}
          {!hideReject && (
            <Grid item>
              <Button
                variant="contained"
                color="grey"
                type="button"
                loading={loading}
                disabled={loading}
                onClick={onReject}
              >
                Rechazar
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={loading}
              disabled={loading}
            >
              Validar
            </Button>
          </Grid>
        </Grid>
      </form>
    </Root>
  );
};

export default ValidateData;
